import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import withHeadTag from '../../../common/HeadComponent';
import PageError from '../../../common/PageError';
import HubPageLayout from '../../../common/hub/HubPageLayout';
import { PaymentFailureAlertBox } from '../../../components/hub/home/InfoBox';
import { InitialState } from '../../../types/hubTypes';
import { getPaymentFailureAlertInfo } from '../../../util/helper';
import { isPaymentFailed } from '../../../util/helpers';
import { TabNav, TabNavItem } from '../../../v2/common/TabNav';
import { CancelledSessions, CompletedSessions, UpcomingSessions } from './Categories';
import SessionFilter from './SessionFilter';
import { SESSION_CATEGORY } from './types';
import useSessionContext, { withSessionContextProvider } from './useSessionContext';
import CustomerSupportInfo from '../../../v2/common/CustomerSupportInfo';

const Sessions = () => {
  const [activeCategory, setActiveCategory] = useState(SESSION_CATEGORY.UPCOMING);
  const { filteredSessions, fetchSessionIsError: errorSessions } = useSessionContext();
  const location = useLocation();
  const history = useHistory();
  const { upcoming = [] } = filteredSessions;
  const errorLoading = useSelector((state: InitialState) => state.ui.apiState.hubCompletedSessionsPage.fetchError);

  // Set initial category based on URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && Object.values(SESSION_CATEGORY).includes(hash)) {
      setActiveCategory(hash);
    }
  }, [location.hash]);

  const upcomingSessionsWithFailedPayment = useMemo(
    () => upcoming?.filter((sesh) => isPaymentFailed(sesh.paymentStatus)),
    [upcoming]
  );

  const paymentFailureAlert = useMemo(
    () => getPaymentFailureAlertInfo(upcomingSessionsWithFailedPayment),
    [upcomingSessionsWithFailedPayment]
  );

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    // Reflect the active tab in the URL hash so it's shareable and survives a
    // refresh (the effect above reads it back on load).
    history.replace({ ...location, hash: `#${category}` });
  };

  const sessionTabs: TabNavItem<string>[] = [
    { value: SESSION_CATEGORY.UPCOMING, label: 'Upcoming' },
    { value: SESSION_CATEGORY.COMPLETED, label: 'Completed' },
    { value: SESSION_CATEGORY.CANCELLED, label: 'Cancelled' },
  ];

  const renderActiveCategory = useMemo(() => {
    switch (activeCategory) {
      case SESSION_CATEGORY.UPCOMING:
        return <UpcomingSessions />;
      case SESSION_CATEGORY.COMPLETED:
        return <CompletedSessions />;
      case SESSION_CATEGORY.CANCELLED:
        return <CancelledSessions />;
      default:
        return null;
    }
  }, [activeCategory]);

  return (
    <HubPageLayout>
      <div className="mb-8">
        <CustomerSupportInfo />
      </div>
      {(errorSessions || errorLoading) && <PageError activePage="hub-page" retryEnabled={false} />}
      {!errorSessions && !errorLoading && (
        <div className="mb-10">
          {paymentFailureAlert && activeCategory === SESSION_CATEGORY.UPCOMING && (
            <div className="mb-lg-11 mb-10">
              <PaymentFailureAlertBox message={paymentFailureAlert} />
            </div>
          )}
          <TabNav
            tabs={sessionTabs}
            activeTab={activeCategory}
            onTabChange={handleCategoryChange}
            ariaLabel="Session categories"
            className="mb-5"
          />
          <SessionFilter filterClassName="pt-3 pb-6 lg:pb-10" />
          {renderActiveCategory}
        </div>
      )}
    </HubPageLayout>
  );
};

export default withSessionContextProvider(withHeadTag(Sessions));
